import DOMPurify from 'dompurify';
import api from '@/lib/api';

// Minimal styling for mammoth's docx->HTML output, scoped via Tailwind's
// arbitrary-variant child selectors so we don't need the typography plugin
// for what is otherwise a one-off rendering surface.
const RICH_TEXT_CLASSES = [
  '[&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h1:first-child]:mt-0',
  '[&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2',
  '[&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1',
  '[&_p]:mb-2 [&_p:last-child]:mb-0',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2',
  '[&_li]:mb-1',
  '[&_strong]:font-semibold',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:mb-3',
  '[&_th]:border [&_th]:border-zinc-200 [&_th]:bg-zinc-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_td]:border [&_td]:border-zinc-200 [&_td]:px-2 [&_td]:py-1',
  '[&_a]:text-[#0055FF] [&_a]:underline',
].join(' ');

/** True when a policy record has a renderable "rich" view (formatted HTML
 * from a docx upload, or an original PDF we can show inline) rather than
 * only the extracted plain text. */
export function hasPolicyRichContent(policy) {
  if (!policy) return false;
  if (policy.content_html) return true;
  return Boolean(policy.has_file && policy.filename?.toLowerCase().endsWith('.pdf'));
}

/** Renders the sanitized docx->HTML content, or an inline PDF viewer,
 * for a policy record. Callers should check hasPolicyRichContent(policy)
 * first and fall back to plain text (policy.content) otherwise. */
export function PolicyRichContent({ policy, heightClass = 'h-[600px]', className = '' }) {
  if (!policy) return null;

  if (policy.content_html) {
    return (
      <div
        className={`${heightClass} overflow-auto bg-white border border-zinc-200 rounded p-4 text-sm text-zinc-700 ${RICH_TEXT_CLASSES} ${className}`}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy.content_html) }}
      />
    );
  }

  return (
    <iframe
      src={api.getPolicyFileViewUrl(policy.id)}
      title={`${policy.filename || 'Policy document'} preview`}
      className={`w-full ${heightClass} border border-zinc-200 rounded bg-white ${className}`}
    />
  );
}

/** Download URL for a policy's original uploaded file, preferring the
 * new policy_files-backed endpoint (works regardless of object storage
 * being enabled) and falling back to the legacy file_url. Returns null
 * when neither is available (e.g. the hardcoded placeholder policy). */
export function getPolicyDownloadHref(policy) {
  if (!policy || policy.id === 'legacy') return null;
  if (policy.has_file) return api.getPolicyFileDownloadUrl(policy.id);
  return policy.file_url || null;
}
